/**
 * Permission helper utilities for team member access control
 * 
 * This provides the FRONTEND layer of permission checking (UX)
 * Backend middleware provides the SECURITY layer
 */

/**
 * Check if current user has a specific permission
 * @param {Object} user - User object from localStorage
 * @param {string} resource - Resource type: 'leads' | 'consultations' | 'analytics'
 * @param {string} action - Action type: 'view' | 'edit' | 'delete' | 'export' | 'add_comments' | 'approve_reject' | 'view_own' | 'view_team'
 * @returns {boolean} - True if user has permission
 */
export function hasPermission(user, resource, action) {
  // Admin role has all permissions
  if (user?.role === 'admin') {
    return true;
  }

  // Check if user object and permissions exist
  if (!user || !user.permissions || !user.permissions[resource]) {
    return false;
  }

  // Return the specific permission value
  return user.permissions[resource][action] === true;
}

/**
 * Check if user can view a resource
 */
export function canView(user, resource) {
  return hasPermission(user, resource, 'view');
}

/**
 * Check if user can edit a resource
 */
export function canEdit(user, resource) {
  return hasPermission(user, resource, 'edit');
}

/**
 * Check if user can delete a resource
 */
export function canDelete(user, resource) {
  return hasPermission(user, resource, 'delete');
}

/**
 * Check if user can export a resource
 * Handles both old boolean format and new object format
 */
export function canExport(user, resource) {
  // Admin role has all permissions
  if (user?.role === 'admin') {
    return true;
  }

  // Check if user object and permissions exist
  if (!user || !user.permissions || !user.permissions[resource]) {
    return false;
  }

  const exportPerm = user.permissions[resource]['export'];
  
  // Handle old boolean format (backward compatibility)
  if (typeof exportPerm === 'boolean') {
    return exportPerm;
  }
  
  // Handle new object format {enabled: boolean, fields: []}
  if (typeof exportPerm === 'object' && exportPerm !== null) {
    return exportPerm.enabled === true;
  }
  
  return false;
}

/**
 * Check if user can add comments
 */
export function canAddComments(user, resource) {
  return hasPermission(user, resource, 'add_comments');
}

/**
 * Check if user can approve/reject consultations
 */
export function canApproveReject(user) {
  return hasPermission(user, 'consultations', 'approve_reject');
}

/**
 * Check if user can view team analytics
 */
export function canViewTeamAnalytics(user) {
  return hasPermission(user, 'analytics', 'view_team');
}

/**
 * Check if user can view own analytics
 */
export function canViewOwnAnalytics(user) {
  return hasPermission(user, 'analytics', 'view_own');
}

/**
 * Check if user is admin
 */
export function isAdmin(user) {
  return user?.role === 'admin';
}
